import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, RefreshCw } from 'lucide-react';
import MainService from '../../services/MainService';
import { AlertBanner, Field, SectionCard, inputClass } from './accountingUi';
import { formatMoney, parseApiList } from '../../utils/accountingHelpers';
import { buildStatementDateRange, indentByLevel } from '../../utils/accountingReportHelpers';
import { orderDateToInputValue } from '../../utils/dateLocal';

const REPORT_CONFIG = {
  'trial-balance': {
    title: 'Trial Balance',
    subtitle: 'Debit and credit balances for all accounts.',
    api: 'GetTrialBalance',
  },
  'balance-sheet': {
    title: 'Balance Sheet',
    subtitle: 'Assets and liabilities as of the selected date.',
    api: 'GetBalanceSheet',
  },
  'profit-loss': {
    title: 'Profit & Loss',
    subtitle: 'Revenue and expenses for the selected period.',
    api: 'GetProfitLoss',
  },
};

const yearStartInput = () => {
  const now = new Date();
  return orderDateToInputValue(new Date(now.getFullYear(), 0, 1));
};

const FinancialStatementReport = () => {
  const { reportKey } = useParams();
  const config = REPORT_CONFIG[reportKey] || REPORT_CONFIG['trial-balance'];

  const [dateFrom, setDateFrom] = useState(yearStartInput);
  const [dateTo, setDateTo] = useState(() => orderDateToInputValue(new Date()));
  const [level, setLevel] = useState(4);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          openingDebit: acc.openingDebit + Number(row.OpeningDebit || 0),
          openingCredit: acc.openingCredit + Number(row.OpeningCredit || 0),
          periodDebit: acc.periodDebit + Number(row.TotalDebit || 0),
          periodCredit: acc.periodCredit + Number(row.TotalCredit || 0),
          balanceDebit: acc.balanceDebit + Number(row.BalanceDebit || 0),
          balanceCredit: acc.balanceCredit + Number(row.BalanceCredit || 0),
        }),
        {
          openingDebit: 0,
          openingCredit: 0,
          periodDebit: 0,
          periodCredit: 0,
          balanceDebit: 0,
          balanceCredit: 0,
        }
      ),
    [rows]
  );

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = buildStatementDateRange(new Date(dateFrom), new Date(dateTo), { Level: Number(level) });
      const response = await MainService[config.api](payload);
      setRows(parseApiList(response));
    } catch (err) {
      setError(err?.message || `Failed to load ${config.title.toLowerCase()}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [config.api, config.title, dateFrom, dateTo, level]);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{config.title}</h1>
          <p className="text-sm text-gray-500">{config.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={loadRows}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <AlertBanner message={error} />

      <SectionCard title="Filters">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Field label="Date from">
            <input type="date" className={inputClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field label="Date to">
            <input type="date" className={inputClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          <Field label="Account level">
            <select className={inputClass} value={level} onChange={(e) => setLevel(Number(e.target.value))}>
              <option value={1}>Level 1</option>
              <option value={2}>Level 2</option>
              <option value={3}>Level 3</option>
              <option value={4}>Level 4 (detail)</option>
            </select>
          </Field>
          <div className="flex items-end">
            <button
              type="button"
              onClick={loadRows}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
            >
              <CheckCircle className="h-4 w-4" />
              Generate
            </button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={config.title} bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-3 py-3 text-left">Code</th>
                <th className="px-3 py-3 text-left">Account</th>
                <th className="px-3 py-3 text-right">Opening Dr</th>
                <th className="px-3 py-3 text-right">Opening Cr</th>
                <th className="px-3 py-3 text-right">Period Dr</th>
                <th className="px-3 py-3 text-right">Period Cr</th>
                <th className="px-3 py-3 text-right">Balance Dr</th>
                <th className="px-3 py-3 text-right">Balance Cr</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                    Generating report...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                    No data yet. Set the date range and click Generate.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={`${row.Id}-${row.AccountCode}-${row.Level}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{row.AccountCode || '-'}</td>
                    <td className="px-3 py-2 font-medium text-gray-900" style={indentByLevel(row.Level)}>
                      {row.Name || '-'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.OpeningDebit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.OpeningCredit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.TotalDebit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.TotalCredit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{formatMoney(row.BalanceDebit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{formatMoney(row.BalanceCredit)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 ? (
              <tfoot className="bg-gray-50 font-semibold text-gray-800">
                <tr>
                  <td colSpan={2} className="px-3 py-3 text-right text-xs uppercase tracking-wide">
                    Totals
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.openingDebit)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.openingCredit)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.periodDebit)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.periodCredit)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.balanceDebit)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.balanceCredit)}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

export default FinancialStatementReport;
